// src/components/CityScene.tsx

import React, { useCallback, useEffect, useRef } from 'react';
import { Html } from '@react-three/drei';
import { useCityData, type BuildingForRender, type RoadForRender } from '../hooks/useCityData';
import Building from './Building';
import Road from './Road';

// ----------------------------------------------------------------------
// Tipos de props
// ----------------------------------------------------------------------

export interface CitySceneProps {
  /** Latitud central */
  lat: number;
  /** Longitud central */
  lng: number;
  /** Radio de búsqueda en metros */
  radius?: number;
  /** Si se deben incluir calles en el renderizado */
  includeRoads?: boolean;
  /** Callback cuando se selecciona un edificio */
  onSelectBuilding?: (building: BuildingForRender | null) => void;
  /** ID del edificio seleccionado (controlado externamente) */
  selectedBuildingId?: number | string | null;
  /** Si se debe mostrar el suelo */
  showGround?: boolean;
  /** URL de un JSON local (public/data/*.json) */
  seedUrl?: string;
  /** Si se deben mostrar los edificios */
  showBuildings?: boolean;
  /** Señal de recarga (incrementar para recargar desde la API) */
  reloadSignal?: number;
  /** Callback cuando se selecciona una calle */
  onSelectRoad?: (road: RoadForRender | null) => void;
  /** ID de la calle seleccionada (controlado externamente) */
  selectedRoadId?: number | string | null;
  /** Callback cuando las calles están listas (para exportación) */
  onRoadsReady?: (roads: RoadForRender[]) => void;
  /** Callback cuando los edificios están listos (para exportación) */
  onBuildingsReady?: (buildings: BuildingForRender[]) => void;
  /** IDs ocultos de la representación 3D (edificios o calles borrados visualmente) */
  hiddenIds?: ReadonlySet<number | string>;
}

// ----------------------------------------------------------------------
// Componente principal de la escena de ciudad
// ----------------------------------------------------------------------

const CityScene: React.FC<CitySceneProps> = ({
  lat,
  lng,
  radius = 500,
  includeRoads = true,
  onSelectBuilding,
  selectedBuildingId = null,
  showGround = true,
  seedUrl,
  showBuildings = true,
  reloadSignal = 0,
  onSelectRoad,
  selectedRoadId = null,
  onRoadsReady,
  onBuildingsReady,
  hiddenIds,
}) => {
  // Cargar datos usando el hook
  const { data, loading, error, reload } = useCityData({
    lat,
    lng,
    radiusMeters: radius,
    includeRoads,
    seedUrl,
  });

  // Referencias estables a callbacks, para poder usarlos dentro de efectos
  // sin incluirlos en las dependencias (evita re-ejecuciones accidentales).
  const reloadRef = useRef(reload);
  useEffect(() => {
    reloadRef.current = reload;
  }, [reload]);

  const onRoadsReadyRef = useRef(onRoadsReady);
  useEffect(() => {
    onRoadsReadyRef.current = onRoadsReady;
  }, [onRoadsReady]);

  const onBuildingsReadyRef = useRef(onBuildingsReady);
  useEffect(() => {
    onBuildingsReadyRef.current = onBuildingsReady;
  }, [onBuildingsReady]);

  // Actualizar desde el JSON local cuando se pulsa el botón de refrescar
  useEffect(() => {
    if (reloadSignal > 0) {
      reloadRef.current();
    }
  }, [reloadSignal]);

  // Reportar las calles cargadas para permitir exportación
  useEffect(() => {
    if (data) {
      onRoadsReadyRef.current?.(data.roads);
      onBuildingsReadyRef.current?.(data.buildings);
    }
  }, [data]);

  // Manejar selección de edificio
  const handleBuildingSelect = useCallback(
    (building: BuildingForRender) => {
      if (onSelectBuilding) {
        onSelectBuilding(building);
      }
    },
    [onSelectBuilding]
  );

  // Manejar selección de calle
  const handleRoadSelect = useCallback(
    (road: RoadForRender) => {
      if (onSelectRoad) {
        onSelectRoad(road);
      }
    },
    [onSelectRoad]
  );

  // Si hay error, mostrar mensaje en el centro
  if (error) {
    return (
      <Html center>
        <div style={{ background: 'white', padding: '15px', borderRadius: '8px', textAlign: 'center' }}>
          <p style={{ color: 'red', margin: '0 0 10px 0' }}>⚠️ Error al cargar datos</p>
          <p style={{ margin: '0 0 10px 0' }}>{error.message}</p>
          <button
            onClick={reload}
            style={{
              padding: '6px 12px',
              background: '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Reintentar
          </button>
        </div>
      </Html>
    );
  }

  // Si está cargando, mostrar spinner
  if (loading) {
    return (
      <Html center>
        <div style={{ background: 'white', padding: '15px', borderRadius: '8px', textAlign: 'center' }}>
          <p style={{ margin: 0 }}>🔄 Cargando datos de la ciudad...</p>
        </div>
      </Html>
    );
  }

  // Si no hay datos, no renderizar nada (puede pasar momentáneamente)
  if (!data) return null;

  return (
    <group>
      {/* Suelo base (opcional) */}
      {showGround && (
        <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.1, 0]} receiveShadow>
          <planeGeometry args={[radius * 4, radius * 4]} />
          <meshStandardMaterial color="#4a7c59" />
        </mesh>
      )}

      {/* Renderizar edificios */}
      {showBuildings &&
        data.buildings
          .filter((b) => !hiddenIds?.has(b.id))
          .map((building) => (
            <Building
              key={building.id}
              building={building}
              selected={building.id === selectedBuildingId}
              onSelect={handleBuildingSelect}
            />
          ))}

      {/* Renderizar calles */}
      {includeRoads &&
        data.roads
          .filter((r) => !hiddenIds?.has(r.id))
          .map((road) => (
            <Road
              key={road.id}
              road={road}
              selected={road.id === selectedRoadId}
              onSelect={handleRoadSelect}
            />
          ))}
    </group>
  );
};

export default CityScene;